import React, { useRef, useState, useContext, useEffect } from 'react';
import {
  IconHeadset,
  IconChartBar,
  IconLayoutDashboard,
  IconSettings,
} from '@tabler/icons-react';
import { MouseEventHandler } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Group, Drawer, Burger, ScrollArea } from '@mantine/core';

import routes from 'utils/routes';
import Navlink from './NavbarLink';
import UserButton from './UserButton';
import SetupProgressTracker from './SetupProgressTracker';
import UserProfile from '../header/UserProfile';
import { NewtCloudLogo } from 'img/NewtCloudLogo';
import AuthContext from 'context/AuthContext';
import BillingContext from 'context/BillingContext';
import { AccessLevel } from 'interfaces/GetUserResult';
import { UpgradePopover } from 'components/upgrade';

import classes from './index.module.scss';

const navLinks = [
  {
    icon: IconLayoutDashboard,
    link: routes.dashboardPath.href,
    label: 'Dashboard',
  },
  {
    icon: IconHeadset,
    link: routes.receptionPath.href,
    label: routes.receptionPath.title,
  },
  {
    icon: IconChartBar,
    link: routes.reportingPath.href,
    label: 'Reporting',
  },
  {
    icon: IconSettings,
    link: routes.settingsPath.href,
    label: 'Settings',
  },
];

interface SideNavProps {
  opened: boolean;
  close: () => void;
  toggle: MouseEventHandler<HTMLButtonElement>;
}

const SideNav = ({ opened, toggle, close }: SideNavProps) => {
  const { data: { account } } = useContext(AuthContext);
  const {
    actions: { setPricingOpened },
  } = useContext(BillingContext);
  const isPremium = account?.access_level === AccessLevel.PRO;
  const [showUpgradePopover, setShowUpgradePopover] = useState(false);
  const reportingLinkRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);
  const reportingDivRef = useRef<HTMLDivElement>(null);

  // Close popover when clicking outside
  useEffect(() => {
    if (!showUpgradePopover) return;
    
    const handleClickOutside = (event: MouseEvent) => {
      if ((reportingLinkRef.current && reportingLinkRef.current.contains(event.target as Node)) ||
          (reportingDivRef.current && reportingDivRef.current.contains(event.target as Node)) ||
          document.querySelector('.mantine-Popover-dropdown')?.contains(event.target as Node)) {
        return;
      }
      setShowUpgradePopover(false);
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUpgradePopover]);

  const handleReportingClick = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>) => {
    if (!isPremium) {
      e.preventDefault();
      e.stopPropagation(); // Prevent event bubbling
      setShowUpgradePopover((prev) => !prev);
      // Analytics event
      if (typeof window !== 'undefined' && (window as any).analytics && typeof (window as any).analytics.track === 'function') {
        (window as any).analytics.track('reporting_paywall_shown_nav');
      }
      return;
    }
    if (close) close();
  };

  const handleUpgrade = () => {
    // Close popover
    setShowUpgradePopover(false);
    
    // Open pricing modal using BillingContext
    setPricingOpened(true);
    
    // Track upgrade button click
    if (typeof window !== 'undefined' && (window as any).analytics && typeof (window as any).analytics.track === 'function') {
      (window as any).analytics.track('reporting_nav_upgrade_button_clicked');
    }
  };

  const links = navLinks.map((item) => {
    if (item.label === 'Reporting') {
      return (
        <UpgradePopover
          key={item.label}
          opened={showUpgradePopover}
          onClose={() => setShowUpgradePopover(false)}
          upgradeTitle="Unlock Reporting & Analytics"
          upgradeDescription="Use AI to maximize customer insights. Unlock full call details, transcripts, recordings, and analytics to analyze every conversation effortlessly. Upgrade to Pro to access reporting."
          onUpgrade={handleUpgrade}
        >
          <div ref={reportingDivRef}>
            <Navlink
              {...item}
              closeNav={close}
              onClick={handleReportingClick}
              navRef={reportingLinkRef}
              isPremium={isPremium}
            />
          </div>
        </UpgradePopover>
      );
    }
    return <Navlink {...item} key={item.label} closeNav={close} isPremium={isPremium} />;
  });

  return (
    <>
      <div className={classes.navbarContainer}>
        <div className={classes.header}>
          <RouterLink to={routes.dashboardPath.href}>
            <div className={classes.logo}>
              <NewtCloudLogo />
            </div>
          </RouterLink>
        </div>

        <ScrollArea className={classes.links}>
          <div className={classes.linksInner}>{links}</div>
        </ScrollArea>

        <div className={classes.footer}>
          <SetupProgressTracker />
          <UserButton />
        </div>
      </div>

      <Drawer
        size="100%"
        padding={0}
        opened={opened}
        onClose={close}
        hiddenFrom="sm"
        withCloseButton={false}
      >
        <div className={classes.navbarContainer}>
          <div className={classes.header}>
            <Group>
              <Burger
                size="sm"
                hiddenFrom="sm"
                opened={opened}
                onClick={toggle}
              />
              <RouterLink to={routes.dashboardPath.href}>
                <div className={classes.logo}>
                  <NewtCloudLogo />
                </div>
              </RouterLink>
            </Group>
          </div>

          <ScrollArea className={classes.links}>
            <Box mx="xl" mt="lg" className={classes.user}>
              <UserProfile />
            </Box>
            <div className={classes.linksInner}>{links}</div>
          </ScrollArea>

          <div className={classes.footer}>
            <SetupProgressTracker />
            <UserButton />
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default SideNav;
